import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Clock, Euro, TrendingUp, Zap } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-8 text-sm text-zinc-500">Chargement analytics...</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="analytics-page">
      <div className="mb-10">
        <span className="overline">Analytics & ROI</span>
        <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">Impact de vos automatisations</h1>
        <p className="text-zinc-600 mt-2">Calculé sur la base d'un coût horaire moyen de €{data.hourly_cost_assumption}/h en PME européenne.</p>
      </div>

      {/* ROI Hero */}
      <div className="bg-black text-white rounded-sm p-10 mb-8 grain" data-testid="roi-hero-card">
        <span className="overline text-zinc-400">ROI généré</span>
        <div className="flex items-baseline gap-3 mt-3">
          <span className="font-display text-7xl font-semibold tracking-tighter">€{data.roi_euros}</span>
          <span className="text-zinc-400">économisés au total</span>
        </div>
        <p className="text-zinc-400 mt-4 max-w-xl">
          Basé sur {data.hours_saved}h de travail automatisé · {data.total_executions} exécutions réussies
        </p>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <StatCard label="Heures économisées" value={`${data.hours_saved}h`} icon={Clock}/>
        <StatCard label="Exécutions réussies" value={data.successful_executions} icon={Zap}/>
        <StatCard label="Workflows actifs" value={data.active_workflows} icon={TrendingUp}/>
        <StatCard label="Coût main-d'œuvre" value={`€${data.hourly_cost_assumption}/h`} icon={Euro}/>
      </div>

      {/* Timeseries chart */}
      <div className="bg-white border border-zinc-200 rounded-sm p-8" data-testid="executions-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <span className="overline">14 derniers jours</span>
            <h2 className="font-display text-2xl font-medium tracking-tight mt-2">Exécutions quotidiennes</h2>
          </div>
        </div>
        <div className="w-full h-72">
          <ResponsiveContainer>
            <LineChart data={data.timeseries} margin={{ top: 20, right: 10, bottom: 0, left: -20 }}>
              <CartesianGrid stroke="#E4E4E7" strokeDasharray="3 3"/>
              <XAxis dataKey="date" stroke="#71717A" fontSize={10} tickFormatter={(d) => d.slice(5)}/>
              <YAxis stroke="#71717A" fontSize={10} allowDecimals={false}/>
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 }} />
              <Line type="monotone" dataKey="executions" stroke="#0038FF" strokeWidth={2} dot={{ r: 3 }}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <Icon size={18} strokeWidth={1.5} className="text-zinc-500"/>
        <span className="overline">{label}</span>
      </div>
      <p className="font-display text-3xl font-semibold tracking-tighter">{value}</p>
    </div>
  );
}
